// Allow Vite HMR to accept updates to this module so component changes
// propagate via React Fast Refresh instead of triggering a full page reload.
if (import.meta.hot) {
    import.meta.hot.accept();
}

import { combineReducers, configureStore } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import type { TypedUseSelectorHook } from "react-redux";
import { useDispatch, useSelector } from "react-redux";

import accountingReducer from "./accountingStore";
import adminPanelReducer from "./adminPanelSlice";
import advanceCustomStatusesReducer from "./advanceCustomStatusStore";
import advancePaymentReducer from "./advancePaymentSlice";
import agreementReducer from "./agreementsStore";
import analyticsReducer from "./analyticsStore";
import articleReducer from "./articleStore";
import automatedImportsReducer from "./automatedImportStore/index";
import beneficiaryDocumentsOverviewReducer from "./beneficiaryDocumentsOverviewStore";
import beneficiaryDocumentTypesReducer from "./beneficiaryDocumentTypesStore";
import beneficiaryReducer from "./beneficiaryStore";
import bexioReducer from "./bexioStore";
import bpTermsReducer from "./bpTermStore";
import brandReducer from "./brandStore";
import calculationBaseColumnLabelsReducer from "./calculationBaseColumnLabelsStore";
import channelsReducer from "./channelStore";
import type { CompanyGroupRepository } from "./companyGroups";
import { CompanyGroupApi } from "./companyGroups/companyGroupsApi";
import { CompanyGroupInMem, companyGroupsFixtures } from "./companyGroups/companyGroupsInMem";
import type { AdvanceProvisionRepository } from "./advanceProvision";
import { AdvanceProvisionApi } from "./advanceProvision/advanceProvisionApi";
import { AdvanceProvisionInMem } from "./advanceProvision/advanceProvisionInMem";
import companyGroupsReducer from "./companyGroups/companyGroupsStore";
import contributionReducer from "./contributionStore";
import customFieldsReducer from "./customFieldsStore";
import dataImportReducer from "./dataImportSlice";
import documentReducer from "./documentStore";
import filesHistoryReducer from "./filesHistorySlice";
import integrationsReducer from "./integrationsStore";
import invoicePercentagesReducer from "./invoicePercentages";
import jobsReducer from "./jobsStore";
import localesReducer from "./localesStore";
import managementReducer from "./managementStore";
import notesReducer from "./notesStore";
import notificationReducer from "./notificationStore";
import partnerStatementsReducer from "./partnerStatementsStore";
import paymentReducer from "./paymentSlice";
import periodReducer from "./periodStore";
import permissionsReducer from "./permissionsStore";
import type { PriceHistoryRepository } from "./priceHistory";
import { priceHistoryFixtures, PriceHistoryInMem } from "./priceHistory/priceHistoryInMem";
import priceHistoryReducer from "./priceHistory/priceHistoryStore";
import servicesReducer from "./servicesStore";
import settingsReducer, { appMaintenanceReducer, closingPeriodReducer } from "./settingsStore";
import slackReducer from "./slackStore";
import statementsReducer from "./statementsStore";
import stockReducer from "./stockStore";
import termReducer from "./termStore";
import termTemplatesReducer from "./termTemplatesStore";
import titleFormatTypesReducer from "./titleFormatTypeStore";
import titlesReducer from "./titleStore";
import transfereesReducer from "./transfereesStore";
import transferTermSalesReducer from "./transferTermSalesStore";
import transferTermsReducer from "./transferTermsStore";
import worksReducer from "./workStore";

const appReducer = combineReducers({
    titles: titlesReducer,
    titleFormatTypes: titleFormatTypesReducer,
    works: worksReducer,
    beneficiary: beneficiaryReducer,
    bexio: bexioReducer,
    channels: channelsReducer,
    term: termReducer,
    management: managementReducer,
    period: periodReducer,
    settings: settingsReducer,
    statements: statementsReducer,
    adminPanel: adminPanelReducer,
    advanceCustomStatuses: advanceCustomStatusesReducer,
    advancePayment: advancePaymentReducer,
    payment: paymentReducer,
    filesHistory: filesHistoryReducer,
    dataImport: dataImportReducer,
    documents: documentReducer,
    notes: notesReducer,
    closingPeriod: closingPeriodReducer,
    appMaintenance: appMaintenanceReducer,
    stock: stockReducer,
    notification: notificationReducer,
    bpTerm: bpTermsReducer,
    slack: slackReducer,
    accounting: accountingReducer,
    article: articleReducer,
    brands: brandReducer,
    contribution: contributionReducer,
    agreements: agreementReducer,
    analytics: analyticsReducer,
    integrations: integrationsReducer,
    partnerStatements: partnerStatementsReducer,
    customFields: customFieldsReducer,
    beneficiaryDocumentTypes: beneficiaryDocumentTypesReducer,
    beneficiaryDocumentsOverview: beneficiaryDocumentsOverviewReducer,
    jobs: jobsReducer,
    termTemplates: termTemplatesReducer,
    transferees: transfereesReducer,
    transferTerms: transferTermsReducer,
    transferTermSales: transferTermSalesReducer,
    invoicePercentages: invoicePercentagesReducer,
    services: servicesReducer,
    calculationBaseColumnLabels: calculationBaseColumnLabelsReducer,
    automatedImports: automatedImportsReducer,
    locales: localesReducer,
    permissions: permissionsReducer,
    priceHistory: priceHistoryReducer,
    companyGroups: companyGroupsReducer,
});

const rootReducer = (state, action) => {
    if (action.type === "USER/LOGOUT") {
        return appReducer(undefined, action);
    }
    return appReducer(state, action);
};

export type Dependencies = {
    priceHistoryRepository?: PriceHistoryRepository;
    companyGroupRepository: CompanyGroupRepository;
    advanceProvisionRepository: AdvanceProvisionRepository;
};
const getDependencies = (): Dependencies => {
    if (import.meta.env.REACT_APP_USE_IN_MEM_REPOSITORIES === "true") {
        return {
            priceHistoryRepository: new PriceHistoryInMem({ 222553: priceHistoryFixtures }),
            companyGroupRepository: new CompanyGroupInMem(companyGroupsFixtures),
            advanceProvisionRepository: new AdvanceProvisionInMem(),
        };
    }
    return {
        priceHistoryRepository: undefined,
        companyGroupRepository: new CompanyGroupApi(),
        advanceProvisionRepository: new AdvanceProvisionApi(),
    };
};

const sentryReduxEnhancer = Sentry.createReduxEnhancer({});
export const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            thunk: {
                extraArgument: getDependencies(),
            },
            serializableCheck: {
                ignoredPaths: ["period"],
            },
        }),
    devTools: import.meta.env.REACT_APP_ENVIRONMENT === "local",
    enhancers: (defaultEnhancers) => {
        return defaultEnhancers.concat(sentryReduxEnhancer);
    },
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export { createAppAsyncThunk } from "./appAsyncThunk";
